import { useSuspenseQuery } from "@apollo/client";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { z } from "zod";

import { Container } from "@roqua/components/container";
import { Section, SectionHeader } from "@roqua/components/sections";

import { AutoProtocolIcon } from "@/components/icons";
import { PageTitle } from "@/components/PageTitle";
import { BackButton } from "@/components/ui/BackButton";
import { SingleAutoProtocolQuery } from "@/features/ProtocolSubscriptions/queries";
import { RespondentTypeEnum } from "@/generated/gql/graphql";
import { useTranslation } from "@/i18n/client";
import { respondentNameOrLabel } from "@/models/respondent";
import { useSessionContext } from "@/session";

const searchSchema = z.object({
  auto_protocol_id: z.string().optional(),
});

export const Route = createFileRoute(
  "/$organizationKey/$dossierId/protocol_subscriptions/new/$autoProtocolId",
)({
  component: RouteComponent,
  validateSearch: searchSchema,
});

function RouteComponent() {
  const { t } = useTranslation("ProtocolSubscriptions");
  const { organizationKey, dossier, legacyRoutes } = useSessionContext();
  const { autoProtocolId } = Route.useParams();

  const { data } = useSuspenseQuery(SingleAutoProtocolQuery, {
    variables: { organizationKey, autoProtocolId },
  });

  const autoProtocol = data?.organization.autoProtocol;

  const findRespondents = (type: RespondentTypeEnum) =>
    dossier.respondents.filter((r) => r.type === type);

  // Get all eligible respondents for this auto protocol
  const eligibleRespondents = autoProtocol
    ? autoProtocol.subscribableRespondentTypes.flatMap((type) => findRespondents(type))
    : [];

  // If there's exactly one eligible respondent, redirect immediately
  if (autoProtocol && eligibleRespondents.length === 1) {
    window.location.replace(
      legacyRoutes.newProtocolSubscriptionUrl(autoProtocol.id, eligibleRespondents[0].id),
    );
    return null;
  }

  // If we have an auto protocol but no respondent, show respondent selection
  return (
    <>
      <PageTitle title="protocolSubscriptionsNew" />
      <Section variant="header">
        <SectionHeader
          header={t("startNewProtocolSubscription")}
          buttons={<BackButton href="/$organizationKey/$dossierId/measure" />}
        />

        <div className="flex items-center gap-3">
          <AutoProtocolIcon />
          <h4 className="font-medium">{autoProtocol.name}</h4>
        </div>

        {autoProtocol.description && (
          <p className="text-muted-foreground mt-1 text-sm">{autoProtocol.description}</p>
        )}
      </Section>

      <Section>
        <Container>
          <div className="mb-4">
            <h3 className="mb-2 text-lg font-medium">Voor wie wil je dit auto protocol starten?</h3>
          </div>

          <div className="space-y-0">
            {eligibleRespondents.map((respondent) => (
              <a
                key={respondent.id}
                href={legacyRoutes.newProtocolSubscriptionUrl(autoProtocol.id, respondent.id)}
                className="border-border hover:bg-muted/30 group flex cursor-pointer items-center justify-between gap-1 border border-b-0 p-2 transition-all duration-200 first:rounded-t-lg last:rounded-b-lg last:border-b hover:border-primary/50"
              >
                <div className="mr-2 p-2">
                  <AutoProtocolIcon />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-medium transition-colors group-hover:text-primary">
                    {respondentNameOrLabel(respondent) ??
                      t(`common:respondent.type.${respondent.type}`)}
                  </h4>
                </div>
                <ChevronRight className="text-muted-foreground ml-4 h-4 w-4 flex-shrink-0 transition-all group-hover:translate-x-1 group-hover:text-primary" />
              </a>
            ))}
          </div>
        </Container>
      </Section>
    </>
  );
}
